// src/components/modules/demandWLGenerator/base/runLogsTab.tsx
import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Grid, DataGridPro, MuiDataGrid, Mui, MuiIcons, AlertTypes, apiClient } from '@leap4/frontend';
import { runLogsBaseUrl, xroadsMonitoringBaseUrl } from '../../../../config/generals';
import { demandWLGeneratorContext } from './demandWLGeneratorContext';
import DeleteRunLogsModal from './deleteRunLogsModal';
import { formatDateDDMMYYYY, dateToStringFormat } from '../../../utils/functions';

const { Button, Box, Typography, Paper, TextField, Select, MenuItem, FormControl, InputLabel, CircularProgress, Chip, Collapse, IconButton, Tooltip } = Mui;
type SelectChangeEvent<T = string> = Mui.SelectChangeEvent<T>;
type GridColDef<R = any> = MuiDataGrid.GridColDef<R>;
type GridRowSelectionModel = MuiDataGrid.GridRowSelectionModel;
const {
    Add: AddIcon,
    Info: InfoIcon,
    ExpandMore: ExpandMoreIcon,
    ExpandLess: ExpandLessIcon,
    CheckCircle: CheckCircleIcon,
    Error: ErrorIcon,
    ContentCopy: ContentCopyIcon,
} = MuiIcons;

interface RunLog {
    _id: string;
    created: Date;
    templateName: string;
    status: string;
}

interface Filter {
    id: string;
    column: string;
    condition: string;
    value: string;
}

// Helper function to get cell value
const getCellValue = (log: RunLog, column: string): string => {
    switch (column) {
        case "templateName":
            return log.templateName || "";
        case "status":
            return log.status || "";
        case "created":
            return log.created ? dateToStringFormat(log.created as string | number | Date) : "";
        default:
            return "";
    }
};

// Helper function to check if a log matches a filter
const matchesFilter = (log: RunLog, filter: Filter): boolean => {
    if (!filter.value.trim()) {
        return true; // Empty filter matches all
    }

    const cellValue = getCellValue(log, filter.column);
    const cellValueStr = String(cellValue).toLowerCase();
    const filterValueStr = filter.value.toLowerCase();

    switch (filter.condition) {
        case "contains":
            return cellValueStr.includes(filterValueStr);
        case "equals":
            return cellValueStr === filterValueStr;
        case "startsWith":
            return cellValueStr.startsWith(filterValueStr);
        case "endsWith":
            return cellValueStr.endsWith(filterValueStr);
        case "notContains":
            return !cellValueStr.includes(filterValueStr);
        default:
            return true;
    }
};

const RunLogsTab: React.FC = () => {
    const { downloadCsv, downloadBulkCsv, setAlert } = useContext(demandWLGeneratorContext);
    const [runLogs, setRunLogs] = useState<RunLog[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [monitorStatus, setMonitorStatus] = useState<any>(null);
    const [loadingStatus, setLoadingStatus] = useState<boolean>(true);
    const [statusExpanded, setStatusExpanded] = useState<boolean>(false);
    const [queryCopied, setQueryCopied] = useState<boolean>(false);
    const [filters, setFilters] = useState<Filter[]>([
        { id: "1", column: "templateName", condition: "contains", value: "" }
    ]);

    const fetchRunLogs = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(runLogsBaseUrl);
            const processedLogs = response.data.results.map((log: any) => ({
                ...log,
                created: log.created ? new Date(log.created) : null,
            }));

            setRunLogs(processedLogs.filter(log => log.created !== null) as RunLog[]);
        } catch (error) {
            console.error('Error fetching run logs:', error);
            setAlert?.({ msg: 'Error fetching run logs', type: AlertTypes.FAILURE });
        } finally {
            setLoading(false);
        }
    };

    const fetchMonitorStatus = async () => {
        try {
            setLoadingStatus(true);
            const response = await apiClient.get(`${xroadsMonitoringBaseUrl}/status`);
            setMonitorStatus(response.data);
        } catch (error) {
            console.error('Error fetching monitor status:', error);
        } finally {
            setLoadingStatus(false);
        }
    };

    useEffect(() => {
        fetchRunLogs();
        fetchMonitorStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setAlert]);

    const handleDownloadCsv = async (id: string) => {
        try {
            const csvContent = await downloadCsv(id);
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `runLog_${id}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            setAlert?.({ msg: 'Error downloading CSV', type: AlertTypes.FAILURE });
        }
    };

    const handleBulkDownload = async () => {
        if (selectedRows.length === 0) return;
        
        try {
            const blob = await downloadBulkCsv(selectedRows as string[]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `runLogs_bulk_${new Date().toISOString()}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading bulk CSV:', error);
            setAlert?.({ msg: 'Error downloading bulk CSV', type: AlertTypes.FAILURE });
        }
    };

    const handleDeleteClick = () => {
        if (selectedRows.length === 0) {
            setAlert?.({ msg: 'Please select at least one run log to delete', type: AlertTypes.WARNING });
            return;
        }
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (selectedRows.length === 0) return;

        setDeleting(true);
        try {
            // Delete each selected run log
            const deletePromises = (selectedRows as string[]).map(async (id) => {
                try {
                    const response = await apiClient.delete(`${runLogsBaseUrl}/${id}`);
                    // Check if response indicates success
                    if (response.status >= 200 && response.status < 300) {
                        return { id, success: true };
                    } else {
                        const errorMessage = response?.data?.message || `HTTP ${response.status}`;
                        return { id, success: false, error: errorMessage };
                    }
                } catch (error: any) {
                    // Extract error message from various possible formats
                    const errorMessage = error?.response?.data?.message 
                        || error?.response?.data?.error 
                        || error?.response?.data 
                        || error?.message 
                        || `HTTP ${error?.response?.status || 'Unknown'}` 
                        || 'Unknown error';
                    return { id, success: false, error: errorMessage };
                }
            });
            
            const results = await Promise.all(deletePromises);
            const successful = results.filter(r => r.success);
            const failed = results.filter(r => !r.success);
            
            if (failed.length > 0) {
                const errorMessages = failed.map(r => r.error).join('; ');
                setAlert?.({ 
                    msg: `Failed to delete ${failed.length} of ${selectedRows.length} run log(s). Errors: ${errorMessages}`, 
                    type: AlertTypes.FAILURE 
                });
            }
            
            if (successful.length > 0) {
                setAlert?.({ 
                    msg: `Successfully deleted ${successful.length} ${successful.length === 1 ? 'run log' : 'run logs'}`, 
                    type: AlertTypes.SUCCESS 
                });
            }
            
            // Clear selection and refresh the list if any deletions succeeded
            if (successful.length > 0) {
                setSelectedRows([]);
                setDeleteModalOpen(false);
                await fetchRunLogs();
            } else {
                // If all failed, keep modal open so user can try again
                setDeleteModalOpen(false);
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error occurred';
            setAlert?.({ 
                msg: `Error deleting run logs: ${errorMessage}`, 
                type: AlertTypes.FAILURE 
            });
        } finally {
            setDeleting(false);
        }
    };

    // Filter logic - apply all filters (AND logic)
    const filteredRunLogs = useMemo(() => {
        if (!runLogs || runLogs.length === 0) {
            return [];
        }
        
        // If no filters have values, return all logs
        const activeFilters = filters.filter(f => f.value.trim());
        if (activeFilters.length === 0) {
            return runLogs;
        }

        // Apply all filters (AND logic - log must match ALL filters)
        return runLogs.filter((log) => {
            return activeFilters.every(filter => matchesFilter(log, filter));
        });
    }, [runLogs, filters]);

    const handleAddFilter = () => {
        const newFilter: Filter = {
            id: Date.now().toString(),
            column: "templateName",
            condition: "contains",
            value: ""
        };
        setFilters([...filters, newFilter]);
    };

    const handleRemoveFilter = (filterId: string) => {
        if (filters.length > 1) {
            setFilters(filters.filter(f => f.id !== filterId));
        }
    };

    const handleFilterChange = (filterId: string, field: keyof Filter, value: string) => {
        setFilters(filters.map(f => 
            f.id === filterId ? { ...f, [field]: value } : f
        ));
    };

    const handleClearAllFilters = () => {
        setFilters([{ id: "1", column: "templateName", condition: "contains", value: "" }]);
    };


    const filterableColumns = [
        { value: "templateName", label: "Template Name" },
        { value: "status", label: "Status" },
        { value: "created", label: "Run Time" },
    ];

    const filterConditions = [
        { value: "contains", label: "Contains" },
        { value: "equals", label: "Equals" },
        { value: "startsWith", label: "Starts With" },
        { value: "endsWith", label: "Ends With" },
        { value: "notContains", label: "Does Not Contain" },
    ];

    const columns: GridColDef<RunLog>[] = [
        { field: 'templateName', headerName: 'Template Name', flex: 1 },
        {
            field: 'created',
            headerName: 'Run Time',
            flex: 1,
            type: 'dateTime',
            valueFormatter: (value) => {
                if (!value) return 'N/A';
                return formatDateDDMMYYYY(value);
            },
        },
        { field: 'status', headerName: 'Status', flex: 1 },
        {
            field: 'download',
            headerName: 'Download CSV',
            sortable: false,
            minWidth: 150,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleDownloadCsv(params.row._id)}
                >
                    Download
                </Button>
            ),
        },
    ];

    return (
        <Box p={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
                <Typography variant="h6">Run Logs</Typography>
                
                {/* Monitor Status Box */}
                {monitorStatus && (
                    <Paper 
                            elevation={2} 
                            sx={{ 
                                padding: 1.5, 
                                minWidth: 320,
                                maxWidth: 450,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InfoIcon color="primary" fontSize="small" />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        XRoads Monitor Status
                                    </Typography>
                                </Box>
                                <IconButton 
                                    size="small" 
                                    onClick={() => setStatusExpanded(!statusExpanded)}
                                    sx={{ padding: 0.5 }}
                                >
                                    {statusExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Box>
                        
                        {/* Status Indicators */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {monitorStatus.lastRun ? (
                                    <Chip 
                                        icon={monitorStatus.lastRun.success ? <CheckCircleIcon /> : <ErrorIcon />}
                                        label={monitorStatus.lastRun.success ? 'Last Run: Success' : 'Last Run: Failed'}
                                        size="small"
                                        color={monitorStatus.lastRun.success ? 'success' : 'error'}
                                    />
                                ) : (
                                    <Chip 
                                        label="No runs yet" 
                                        size="small" 
                                        color="default"
                                    />
                                )}
                                <Chip 
                                    label={monitorStatus.isActive ? 'Active' : 'Inactive'}
                                    size="small"
                                    color={monitorStatus.isActive ? 'success' : 'error'}
                                />
                            </Box>
                            {monitorStatus.lastRun?.capturedAt && (
                                <Chip 
                                    label={dateToStringFormat(monitorStatus.lastRun.capturedAt)} 
                                    size="small" 
                                    color="success"
                                />
                            )}
                        </Box>

                        {/* Next Run Info */}
                        {monitorStatus.nextRun && (
                            <Typography variant="caption" color="text.secondary">
                                Next Run: {dateToStringFormat(monitorStatus.nextRun)}
                            </Typography>
                        )}

                        {/* Collapsible Details */}
                        <Collapse in={statusExpanded}>
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Schedule: {monitorStatus.cronSchedule}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Cron: {monitorStatus.cronExpression}
                                </Typography>
                                
                                {monitorStatus.lastRun && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 'bold', mt: 1 }}>
                                            Last Run Details:
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Time: {monitorStatus.lastRun.capturedAt ? dateToStringFormat(monitorStatus.lastRun.capturedAt) : 'N/A'}
                                        </Typography>
                                        {monitorStatus.lastRun.message && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Message: {monitorStatus.lastRun.message}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {monitorStatus.lastRun && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 'bold', mt: 1 }}>
                                            Last Query Capture:
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Captured: {monitorStatus.lastRun.capturedAt ? dateToStringFormat(monitorStatus.lastRun.capturedAt) : 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Operation: {monitorStatus.lastRun.operationName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Fields: {monitorStatus.lastRun.fieldsCount} ({monitorStatus.lastRun.fields?.join(', ') || 'N/A'})
                                        </Typography>
                                        {monitorStatus.lastRun.query && (
                                            <Box sx={{ mt: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                                        Query Preview:
                                                    </Typography>
                                                    <Tooltip title={queryCopied ? 'Copied!' : 'Copy full GraphQL query'}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(monitorStatus.lastRun.query);
                                                                setQueryCopied(true);
                                                                setTimeout(() => setQueryCopied(false), 2000);
                                                            }}
                                                            sx={{ padding: 0.25 }}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        display: 'block',
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.7rem',
                                                        wordBreak: 'break-all',
                                                        maxHeight: 100,
                                                        overflow: 'auto',
                                                        backgroundColor: 'rgba(0,0,0,0.05)',
                                                        padding: 0.5,
                                                        borderRadius: 0.5
                                                    }}
                                                >
                                                    {monitorStatus.lastRun.query.substring(0, 500)}
                                                    {monitorStatus.lastRun.query.length > 500 ? '...' : ''}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Collapse>
                    </Paper>
                )}
            </Box>
            
            {/* Filter Box */}
            <Paper elevation={1} sx={{ padding: 2, marginBottom: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Filters</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddFilter}
                            sx={{ height: '40px' }}
                        >
                            Add Filter
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleClearAllFilters}
                            sx={{ height: '40px' }}
                        >
                            Clear All
                        </Button>
                    </Box>
                </Box>
                
                {filters.map((filter, index) => (
                    <Grid container spacing={2} alignItems="center" key={filter.id} sx={{ marginBottom: index < filters.length - 1 ? 2 : 0 }}>
                        <Grid item xs={12} sm={4} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Filter Column</InputLabel>
                                <Select
                                    value={filter.column}
                                    label="Filter Column"
                                    onChange={(e: SelectChangeEvent<string>) => handleFilterChange(filter.id, 'column', e.target.value)}
                                >
                                    {filterableColumns.map((col) => (
                                        <MenuItem key={col.value} value={col.value}>
                                            {col.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Condition</InputLabel>
                                <Select
                                    value={filter.condition}
                                    label="Condition"
                                    onChange={(e: SelectChangeEvent<string>) => handleFilterChange(filter.id, 'condition', e.target.value)}
                                >
                                    {filterConditions.map((cond) => (
                                        <MenuItem key={cond.value} value={cond.value}>
                                            {cond.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Filter Value"
                                value={filter.value}
                                onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
                                placeholder="Enter filter value..."
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={2}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => handleRemoveFilter(filter.id)}
                                disabled={filters.length === 1}
                                sx={{ height: '40px', width: '100%' }}
                            >
                                Remove Filter
                            </Button>
                        </Grid>
                    </Grid>
                ))}
            </Paper>

            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleBulkDownload}
                    disabled={selectedRows.length === 0}
                >
                    Download Selected ({selectedRows.length})
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeleteClick}
                    disabled={selectedRows.length === 0 || deleting}
                >
                    Delete Selected ({selectedRows.length})
                </Button>
            </Box>
            <Box sx={{ height: 600, width: '100%' }}>
                {/* TODO: FIX FOR VERSION 19.2.0 OF FRONTEND LIB - Remove disableColumnPinning once lib is updated */}
                <DataGridPro
                    disableColumnPinning={true}
                    rows={filteredRunLogs}
                    columns={columns}
                    loading={loading}
                    getRowId={(row) => row._id}
                    checkboxSelection
                    onRowSelectionModelChange={(newSelectionModel) => {
                        setSelectedRows(newSelectionModel);
                    }}
                    rowSelectionModel={selectedRows}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'created', sort: 'desc' }],
                        },
                    }}
                />
            </Box>
            <DeleteRunLogsModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                selectedCount={selectedRows.length}
            />
        </Box>
    );
};

export default RunLogsTab;